import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from '@mui/material';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

import DataTable from '../shared/DataTable';
import { Caption, TONE, TagChip } from '../shared/designKit';
import { numFont, plateFont } from '../../utils/fonts';
import { flattenDrfError } from '../../utils/apiUtils';
import { MobileCard, MobileEmptyState, MobileListContainer } from './MobileCards';
import { channelIcon, channelShortTranslationKey, formatCost } from './reminderUtils';

import { can } from '../../utils/access';
// 30-day forward view: who gets what, when, via which channel + SMS cost total.
// Paused rules show what they WOULD do (flagged), same renderer.
export default function UpcomingTab() {
  const { t } = useTranslation();
  const orgId = useUserStore((s) => s.currentOrganization?.organization_id);
  const isAdmin = useUserStore((s) => can(s.currentOrganization, 'service_reminders.manage'));

  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [skipTarget, setSkipTarget] = useState(null);
  const [skipping, setSkipping] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchUpcoming = useCallback(async () => {
    setError(false);
    try {
      const response = await axios.get('/api/reminder-rules/upcoming/', { params: { organization: orgId } });
      setData(response.data);
    } catch (err) {
      console.error('Failed to load upcoming reminders:', err);
      setError(true);
    }
  }, [orgId]);

  useEffect(() => {
    fetchUpcoming();
  }, [fetchUpcoming]);

  const handleSkip = async () => {
    setSkipping(true);
    try {
      if (skipTarget.source === 'scheduled' && skipTarget.send_id) {
        await axios.post(`/api/reminder-sends/${skipTarget.send_id}/skip/`, { organization: orgId });
      } else {
        await axios.post('/api/reminder-rules/skip-upcoming/', {
          rule_id: skipTarget.rule_id,
          maintenance_id: skipTarget.maintenance_id,
          organization: orgId,
        });
      }
      setSnackbar({ open: true, message: t('service_reminders.skipped_ok'), severity: 'success' });
      setSkipTarget(null);
      await fetchUpcoming();
    } catch (err) {
      setSnackbar({ open: true, message: flattenDrfError(err, t('service_reminders.save_error')), severity: 'error' });
    } finally {
      setSkipping(false);
    }
  };

  if (error) {
    return <Alert severity="error">{t('service_reminders.load_error')}</Alert>;
  }
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const dueDate = (item) => (item.due_date ? format(parseISO(item.due_date), 'd.M.yyyy') : '');
  const inactiveTag = <TagChip label={t('service_reminders.inactive')} tone={TONE.slate} />;
  const skipButton = (item) => (
    isAdmin && item.rule_active && (
      <Button size="small" onClick={() => setSkipTarget(item)} sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}>
        {t('service_reminders.skip_button')}
      </Button>
    )
  );

  const columns = [
    {
      key: 'due_date',
      label: t('service_reminders.col_due_date'),
      width: '1%',
      noWrap: true,
      render: (item) => (
        <Typography variant="body2" sx={{ fontWeight: 600, ...numFont }}>{dueDate(item)}</Typography>
      ),
    },
    {
      key: 'customer',
      label: t('service_reminders.col_customer'),
      maxWidth: 220,
      render: (item) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.customer_name}</Typography>,
    },
    {
      key: 'vehicle',
      label: t('service_reminders.col_vehicle'),
      width: '1%',
      noWrap: true,
      render: (item) => (
        <Typography variant="body2" sx={{ ...plateFont, fontWeight: 600 }}>{item.vehicle_registration}</Typography>
      ),
    },
    {
      key: 'rule',
      label: t('service_reminders.col_rule'),
      maxWidth: 240,
      render: (item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: item.rule_active ? 'text.primary' : 'text.secondary' }}>{item.rule_name}</Typography>
          {!item.rule_active && inactiveTag}
        </Box>
      ),
    },
    {
      key: 'channel',
      label: t('service_reminders.col_channel'),
      noWrap: true,
      render: (item) => item.channel && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
          {channelIcon(item.channel, { fontSize: 16 })}
          <Typography variant="body2" sx={{ color: 'text.primary' }}>{t(channelShortTranslationKey(item.channel))}</Typography>
        </Box>
      ),
    },
    {
      key: 'segments',
      label: t('service_reminders.col_segments'),
      width: '1%',
      align: 'right',
      render: (item) => (
        <Typography variant="body2" sx={{ ...numFont, color: item.sms_segments ? 'text.primary' : 'text.disabled' }}>
          {item.sms_segments ?? ''}
        </Typography>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '1%',
      render: (item) => <Box onClick={(e) => e.stopPropagation()}>{skipButton(item)}</Box>,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Caption sx={{ flex: '1 1 320px' }}>{t('service_reminders.upcoming_intro')}</Caption>
        {data.sms_cost_estimate !== null && data.sms_segments_total > 0 && (
          <TagChip
            icon={<SmsOutlinedIcon />}
            tone={TONE.accent}
            label={t('service_reminders.upcoming_sms_cost', {
              segments: data.sms_segments_total,
              cost: formatCost(data.sms_cost_estimate),
            })}
            sx={{ height: 26, ...numFont }}
          />
        )}
      </Box>

      <DataTable
        columns={columns}
        data={data.items}
        emptyIcon={<EventRepeatOutlinedIcon />}
        emptyTitle={t('service_reminders.no_upcoming_title')}
        emptyDescription={t('service_reminders.no_upcoming_description')}
        testIdPrefix="upcoming-reminder"
        tableTestId="upcoming-reminders-table"
      />

      {/* Mobile card list (DataTable hides itself below lg) */}
      <MobileListContainer testId="upcoming-reminders-mobile">
        {data.items.length === 0 ? (
          <MobileEmptyState
            icon={<EventRepeatOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
            title={t('service_reminders.no_upcoming_title')}
            description={t('service_reminders.no_upcoming_description')}
          />
        ) : (
          data.items.map((item) => (
            <MobileCard key={`${item.rule_id}-${item.maintenance_id}`}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', ...numFont }}>{dueDate(item)}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.25, lineHeight: 1.3 }} noWrap>
                    {item.customer_name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25, ...plateFont }}>{item.vehicle_registration}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>
                      {item.rule_name}
                    </Typography>
                    {!item.rule_active && inactiveTag}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'text.secondary' }}>
                    {item.channel && channelIcon(item.channel, { fontSize: 14 })}
                    <Typography sx={{ fontSize: 12.5 }}>
                      {item.channel ? t(channelShortTranslationKey(item.channel)) : ''}
                      {item.sms_segments ? ` · ${item.sms_segments} ${t('service_reminders.col_segments').toLowerCase()}` : ''}
                    </Typography>
                  </Box>
                </Box>
                {skipButton(item)}
              </Box>
            </MobileCard>
          ))
        )}
      </MobileListContainer>

      <Dialog open={!!skipTarget} onClose={() => setSkipTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('service_reminders.skip_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('service_reminders.skip_text', {
              customer: skipTarget?.customer_name,
              vehicle: skipTarget?.vehicle_registration,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipTarget(null)}>{t('Cancel')}</Button>
          <Button variant="contained" onClick={handleSkip} disabled={skipping}>
            {skipping ? <CircularProgress size={20} /> : t('service_reminders.skip_button')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
